import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Globe2, Plane, FileCheck2, Stamp, HeartPulse, Banknote, Home as HomeIcon,
  Bus, Phone, GraduationCap, Sparkles, Users2, ChevronRight, CheckCircle2,
  Circle, ExternalLink, Languages, AlertTriangle,
} from "lucide-react";

const STORAGE_KEY = "intl_checklist_state_v1";

// Chronological checklist — split into 4 phases the user can browse like an accordion.
// Each item has a unique id so progression is persisted in localStorage.
const PHASES = [
  {
    id: "before",
    icon: Plane,
    color: "from-violet-500 to-fuchsia-500",
    title: "Avant ton départ pour la France",
    subtitle: "Préparation depuis ton pays d'origine",
    items: [
      { id: "before-visa", label: "Demander mon visa étudiant (VLS-TS)", help: "Pour les pays hors UE/EEE. 100 % en ligne sur france-visas.gouv.fr.", link: "https://france-visas.gouv.fr" },
      { id: "before-campusfrance", label: "S'inscrire sur Campus France", help: "Obligatoire pour 67 pays (Algérie, Maroc, Chine, Brésil, USA…).", link: "https://www.campusfrance.org" },
      { id: "before-housing", label: "Réserver un logement provisoire (CROUS / Airbnb / hostel)", help: "Tu auras besoin d'une adresse en France pour valider ton visa." },
      { id: "before-insurance", label: "Souscrire une assurance santé internationale (3 mois)", help: "Le temps que ta Sécu étudiante française soit active." },
      { id: "before-docs", label: "Faire traduire et apostiller mon acte de naissance + diplômes", help: "Demandé par les universités et préfectures." },
    ],
  },
  {
    id: "month-1",
    icon: Stamp,
    color: "from-blue-500 to-cyan-500",
    title: "Mois 1 — Tes 30 premiers jours en France",
    subtitle: "Démarches urgentes à faire dès l'arrivée",
    items: [
      { id: "m1-anef", label: "Valider mon VLS-TS sur l'ANEF (50 €)", help: "À FAIRE DANS LES 3 MOIS sinon ton titre devient invalide.", critical: true, link: "https://administration-etrangers-en-france.interieur.gouv.fr" },
      { id: "m1-secu", label: "S'inscrire à la Sécurité sociale étudiante (gratuit)", help: "Indispensable pour être remboursé. Délai 2 à 6 mois.", link: "https://etudiant-etranger.ameli.fr" },
      { id: "m1-bank", label: "Ouvrir un compte bancaire français", help: "Obligatoire pour la CAF, le loyer, les remboursements Sécu." },
      { id: "m1-phone", label: "Souscrire un forfait mobile français", help: "Sosh, Free, RED, Bouygues — à partir de 5 €/mois sans engagement." },
      { id: "m1-transit", label: "Acheter ma carte de transport étudiant", help: "Imagine R (IDF), Pass étudiant TER, Navigo Jeunes, etc." },
    ],
  },
  {
    id: "month-2-3",
    icon: FileCheck2,
    color: "from-emerald-500 to-teal-500",
    title: "Mois 2-3 — Stabilisation",
    subtitle: "Aides, logement définitif, installation",
    items: [
      { id: "m23-caf", label: "Demander les APL (aide au logement)", help: "Jusqu'à 280 €/mois. Délai 4-6 semaines. Demande rétroactive — fais-le vite !", link: "https://www.caf.fr" },
      { id: "m23-housing", label: "Trouver mon logement définitif (CROUS, coloc, particulier)", help: "Visale (gratuit) si tu n'as pas de garant." },
      { id: "m23-visale", label: "Activer Visale (garant gratuit de l'État)", help: "Indispensable pour louer chez un particulier sans garant français.", link: "https://www.visale.fr" },
      { id: "m23-vitale", label: "Recevoir ma carte Vitale", help: "Arrive par la poste après inscription Ameli. 2 à 6 mois selon zone." },
      { id: "m23-doctor", label: "Choisir un médecin traitant", help: "Important pour être bien remboursé." },
    ],
  },
  {
    id: "ongoing",
    icon: GraduationCap,
    color: "from-amber-500 to-orange-500",
    title: "Au quotidien — Profiter de la vie étudiante",
    subtitle: "Travailler, sortir, te faire des amis",
    items: [
      { id: "o-job", label: "Trouver un job étudiant (max 964h/an)", help: "Pas besoin d'autorisation. SMIC horaire net ~9,40 €." },
      { id: "o-community", label: "Rejoindre la communauté Student Hub", help: "Chat & forum traduisibles en 8 langues. Discute avec d'autres Erasmus." },
      { id: "o-discounts", label: "Activer SNCF Avantage Jeune (49 €/an)", help: "-30 % sur tous les trains, rentable dès 3 trajets." },
      { id: "o-mental-health", label: "Garder en tête les numéros d'urgence psy", help: "3114 (24/24, multilingue), Nightline (étudiant, anonyme), Fil Santé Jeunes." },
      { id: "o-aps", label: "Avant la fin du séjour : demander l'APS (12 mois)", help: "Autorisation Provisoire de Séjour pour chercher un emploi après les études." },
    ],
  },
];

export default function EtudiantsInternationaux() {
  const [done, setDone] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
    } catch {
      return new Set();
    }
  });
  const [openPhase, setOpenPhase] = useState("before");

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...done]));
    } catch { /* ignore quota */ }
  }, [done]);

  const toggle = (id) => {
    setDone((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalItems = PHASES.reduce((s, p) => s + p.items.length, 0);
  const completedCount = [...done].filter((id) =>
    PHASES.some((p) => p.items.some((it) => it.id === id))
  ).length;
  const progressPct = Math.round((completedCount / totalItems) * 100);

  return (
    <div className="space-y-8" data-testid="page-etudiants-intl">
      <header>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 text-xs font-semibold text-emerald-800 mb-3">
          <Globe2 size={14} />
          Espace Erasmus · International
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900">
          Bienvenue en France 👋
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl leading-relaxed">
          On va t'accompagner étape par étape. Aucune démarche urgente n'est cachée, rien ne dépend d'un coup de fil à un guichet. Coche chaque case quand c'est fait — ta progression est sauvegardée automatiquement.
        </p>
      </header>

      {/* Progression bar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-700">Ma progression</p>
          <p className="text-sm font-bold text-emerald-700 tabular-nums" data-testid="intl-progress-count">{completedCount} / {totalItems}</p>
        </div>
        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={progressPct} aria-valuemin="0" aria-valuemax="100">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
            data-testid="intl-progress-bar"
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {progressPct === 0 && "Lance-toi : la première étape est la plus importante !"}
          {progressPct > 0 && progressPct < 50 && "C'est parti, continue à ce rythme !"}
          {progressPct >= 50 && progressPct < 100 && "Tu y es presque, dernière ligne droite 💪"}
          {progressPct === 100 && "🎉 Bravo, toutes les étapes sont validées !"}
        </p>
      </div>

      {/* Critical alert */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 flex items-start gap-3" data-testid="intl-critical-alert">
        <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-900">À ne SURTOUT pas oublier dans tes 3 premiers mois</p>
          <p className="text-sm text-amber-800 mt-1 leading-relaxed">
            La validation de ton VLS-TS sur l'ANEF (50 €) est obligatoire dans les 3 mois suivant ton arrivée. Si tu oublies, ton titre de séjour devient invalide. <strong>Mets une alerte sur ton téléphone dès maintenant.</strong>
          </p>
        </div>
      </div>

      {/* Phases */}
      <div className="space-y-4" data-testid="intl-phases">
        {PHASES.map((phase) => {
          const Icon = phase.icon;
          const phaseDone = phase.items.filter((i) => done.has(i.id)).length;
          const phasePct = Math.round((phaseDone / phase.items.length) * 100);
          const isOpen = openPhase === phase.id;
          return (
            <section key={phase.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`intl-phase-${phase.id}`}>
              <button
                onClick={() => setOpenPhase(isOpen ? null : phase.id)}
                className={`w-full flex items-center justify-between gap-4 p-5 text-left transition-colors hover:bg-slate-50 ${isOpen ? "bg-slate-50/50" : ""}`}
                aria-expanded={isOpen}
                data-testid={`intl-phase-trigger-${phase.id}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${phase.color} text-white flex items-center justify-center shadow-md shrink-0`}>
                    <Icon size={22} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-display font-semibold text-slate-900 text-lg leading-tight">{phase.title}</h2>
                    <p className="text-xs text-slate-500 mt-0.5">{phase.subtitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-semibold ${phasePct === 100 ? "text-emerald-700" : "text-slate-500"} tabular-nums`}>
                    {phaseDone}/{phase.items.length}
                  </span>
                  <ChevronRight size={18} className={`text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <ul className="border-t border-slate-100 divide-y divide-slate-100" data-testid={`intl-phase-items-${phase.id}`}>
                  {phase.items.map((item) => {
                    const isDone = done.has(item.id);
                    return (
                      <li key={item.id} className={`p-4 flex items-start gap-3 ${isDone ? "bg-emerald-50/40" : ""}`}>
                        <button
                          onClick={() => toggle(item.id)}
                          className="shrink-0 mt-0.5"
                          aria-pressed={isDone}
                          aria-label={isDone ? "Marquer comme à faire" : "Marquer comme fait"}
                          data-testid={`intl-item-${item.id}`}
                        >
                          {isDone ? (
                            <CheckCircle2 size={22} className="text-emerald-600 fill-emerald-100" />
                          ) : (
                            <Circle size={22} className="text-slate-300 hover:text-emerald-500 transition-colors" />
                          )}
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className={`font-medium text-sm leading-snug ${isDone ? "text-slate-500 line-through" : "text-slate-900"}`}>
                            {item.label}
                            {item.critical && !isDone && (
                              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                <AlertTriangle size={10} /> Urgent
                              </span>
                            )}
                          </p>
                          {item.help && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.help}</p>}
                          {item.link && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1.5"
                            >
                              Lien officiel <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {/* Useful links / community */}
      <section className="grid sm:grid-cols-2 gap-4">
        <Link to="/chat" className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50 p-5 hover:shadow-lg transition-all" data-testid="intl-cta-chat">
          <div className="w-11 h-11 rounded-xl bg-white border border-violet-200 flex items-center justify-center mb-3">
            <Users2 size={20} className="text-violet-600" />
          </div>
          <h3 className="font-display font-semibold text-slate-900 mb-1">Discute avec d'autres Erasmus</h3>
          <p className="text-sm text-slate-600 leading-relaxed">Chat communautaire anonyme, traduisible dans ta langue. Pose tes questions, partage tes galères.</p>
          <p className="text-xs text-violet-700 mt-3 font-semibold inline-flex items-center gap-1">Rejoindre le chat <ChevronRight size={12} /></p>
        </Link>
        <Link to="/communaute" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5 hover:shadow-lg transition-all" data-testid="intl-cta-forum">
          <div className="w-11 h-11 rounded-xl bg-white border border-emerald-200 flex items-center justify-center mb-3">
            <Languages size={20} className="text-emerald-600" />
          </div>
          <h3 className="font-display font-semibold text-slate-900 mb-1">Forum d'annonces traduisible</h3>
          <p className="text-sm text-slate-600 leading-relaxed">Coloc, bons plans, conseils — toutes les annonces sont traduisibles en 8 langues en 1 clic.</p>
          <p className="text-xs text-emerald-700 mt-3 font-semibold inline-flex items-center gap-1">Voir le forum <ChevronRight size={12} /></p>
        </Link>
      </section>

      {/* Disclaimer */}
      <p className="text-xs text-slate-500 leading-relaxed">
        Cette checklist est un guide pédagogique. Pour toute démarche officielle, réfère-toi aux sites gouvernementaux liés (CAF, Ameli, ANEF, etc.). Student Hub n'est pas un service administratif officiel.
      </p>
    </div>
  );
}
